import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading";
import { OwnersOnly } from "../components/OwnersOnly.js";

/**
 * Task 6.6 — taking everything with you.
 *
 * A page rather than a button in a corner, because what is being offered here
 * needs a sentence. `project-scope.md` states no lock-in as an obligation
 * rather than a feature; an obligation that is discoverable only as an
 * unexplained icon is not being kept. The two files answer two different
 * questions and somebody has to be told which is which, or they will download
 * the wrong one and conclude the export is broken.
 *
 * The links are plain anchors with `download`, and there is no fetch, no blob
 * and no JavaScript in the path at all. One origin means the session cookie
 * rides along on its own, and the browser streams the file to disk without it
 * ever passing through the tab's memory — which also means this page cannot
 * fail halfway and leave a half-written file with a 200 behind it.
 *
 * That is also why these two responses are the only ones `api.ts` does not
 * parse against a contract: nothing here is rendered, so there is nothing to
 * disagree about.
 *
 * Task `8.10` added a third and a fourth: the workbook the monthly mail
 * attaches, and the same workbook for a month that has finished — which is
 * what answers an insurer asking what was owned on a date.
 */

/**
 * The last month that has finished, which is the latest one that can be
 * reported on. A month still running has no *as it stood* to describe, and the
 * route refuses one — so the picker refuses it first, where the refusal costs
 * nothing.
 */
function lastCompleteMonth(now: Date): string {
  const month = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1) - 86_400_000);
  return month.toISOString().slice(0, 7);
}

export function Export() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const mayWrite = useMayWrite();

  /**
   * Task 8.10 — the report, for the cellar as it stands or as it stood.
   *
   * The only state on this page, and it stays a plain anchor: the month goes
   * into the href rather than into a fetch, so the download keeps the property
   * the two links above have — the browser streams it to disk, the session
   * cookie rides along on its own, and nothing can fail halfway inside a tab.
   */
  const latest = lastCompleteMonth(new Date());
  const [month, setMonth] = useState(latest);

  // The three downloads here are owner-only in the API and have been since
  // `8.9`: the archive is the whole cellar with its prices in a file that
  // cannot be recalled once it has left, which is a stronger thing than the
  // `guestsSeePrices` setting governs. The links now go before the 403 does.
  if (mayWrite === false) {
    return (
      <section>
        <CellarHeading cellarId={cellarId} current="export" />
        <OwnersOnly cellarId={cellarId} />
      </section>
    );
  }

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="export" />

      <p className="text-muted mt-6 max-w-prose text-sm">{t("export.intro")}</p>

      <ul className="border-line mt-8 border-t">
        <li className="border-line border-b py-5">
          <a
            href={`/api/cellars/${cellarId}/export.csv`}
            download
            className="text-accent font-display text-lg underline-offset-4 hover:underline"
          >
            {t("export.csv")}
          </a>
          <p className="text-muted mt-2 max-w-prose text-sm">{t("export.csvWhat")}</p>
        </li>
        <li className="border-line border-b py-5">
          <a
            href={`/api/cellars/${cellarId}/export.json`}
            download
            className="text-accent font-display text-lg underline-offset-4 hover:underline"
          >
            {t("export.json")}
          </a>
          <p className="text-muted mt-2 max-w-prose text-sm">{t("export.jsonWhat")}</p>
        </li>
        <li className="border-line border-b py-5">
          <a
            href={`/api/cellars/${cellarId}/report.xlsx`}
            download
            className="text-accent font-display text-lg underline-offset-4 hover:underline"
          >
            {t("export.workbook")}
          </a>
          <p className="text-muted mt-2 max-w-prose text-sm">
            {t("export.workbookWhat")}
          </p>
        </li>
        <li className="border-line border-b py-5">
          <h2 className="font-display text-ink text-lg">{t("export.dated")}</h2>
          <p className="text-muted mt-2 max-w-prose text-sm">{t("export.datedWhat")}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="text-muted text-sm" htmlFor="report-month">
              {t("export.month")}
            </label>
            <input
              id="report-month"
              type="month"
              value={month}
              max={latest}
              onChange={(event) => setMonth(event.target.value)}
              className="border-line bg-raised text-ink rounded border px-3 py-2 text-sm"
            />
            <a
              href={`/api/cellars/${cellarId}/report.xlsx?month=${month}`}
              download
              className="text-accent underline-offset-4 hover:underline"
              // A month the browser will not let the route answer. Left in the
              // markup rather than removed, so the page does not reshape itself
              // under a finger that is still choosing.
              aria-disabled={month === "" || month > latest}
            >
              {t("export.datedDownload")}
            </a>
          </div>
        </li>
      </ul>

      <p className="text-muted mt-8 max-w-prose text-sm">{t("export.promise")}</p>
    </section>
  );
}
